import React, { useState } from 'react';
import { useSeller } from '../../context/SellerContext';
import InputField from '../InputField';
import FAQSection from '../FAQSection';
import {
  validateAccountNumber,
  validateIFSCCode,
} from '../../utils/validation';
import { sellerAuthAPI, handleAPIError } from '../../utils/sellerAPI';

const BankDetailsForm = ({ onNext, onPrev }) => {
  const {
    formData,
    updateFormData,
    errors,
    setError,
    clearAllErrors,
    loading,
    setLoading,
  } = useSeller();

  const [bankVerified, setBankVerified] = useState(false);

  const handleAccountChange = (e) => {
    updateFormData('accountNumber', e.target.value.replace(/\D/g, ''));
    clearAllErrors('accountNumber');
    setBankVerified(false);
  };

  const handleConfirmAccountChange = (e) => {
    updateFormData('confirmAccountNumber', e.target.value.replace(/\D/g, ''));
    clearAllErrors('confirmAccountNumber');
  };

  const handleIFSCChange = (e) => {
    let value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
    if (value.length > 11) {
      value = value.slice(0, 11);
    }
    updateFormData('ifscCode', value);
    clearAllErrors('ifscCode');
    setBankVerified(false);
  };

  const handleVerifyBank = async (e) => {
    e.preventDefault();

    // Validation
    const validationErrors = {};
    const accountError = validateAccountNumber(formData.accountNumber);
    const confirmError = validateAccountNumber(formData.confirmAccountNumber);
    const ifscError = validateIFSCCode(formData.ifscCode);

    if (accountError) validationErrors.accountNumber = accountError;
    if (confirmError) validationErrors.confirmAccountNumber = confirmError;
    if (ifscError) validationErrors.ifscCode = ifscError;

    if (formData.accountNumber && formData.confirmAccountNumber) {
      if (formData.accountNumber !== formData.confirmAccountNumber) {
        validationErrors.confirmAccountNumber = 'Account numbers do not match';
      }
    }

    if (Object.keys(validationErrors).length > 0) {
      Object.entries(validationErrors).forEach(([field, error]) => {
        setError(field, error);
      });
      return;
    }

    setLoading(true);
    try {
      const response = await sellerAuthAPI.verifyBankDetails({
        accountNumber: formData.accountNumber,
        ifscCode: formData.ifscCode,
      });

      if (response.data.success) {
        updateFormData('bankDetails', {
          beneficiaryName: response.data.beneficiaryName,
          accountNumber: response.data.accountNumber,
          bankName: response.data.bankName,
          ifscCode: response.data.ifscCode,
        });
        setBankVerified(true);
        clearAllErrors();
      }
    } catch (err) {
      const apiError = handleAPIError(err);
      setError('bankDetails', apiError.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!bankVerified) {
      setError('bankDetails', 'Please verify bank details first');
      return;
    }

    onNext();
  };

  const bankFAQs = [
    {
      question: 'What if I don\'t have bank account in name of business?',
      answer:
        'We can only transfer payments to accounts which are in the registered business name. Please open a new bank account with any bank in your registered business name.',
    },
    {
      question: 'How and when do I get paid?',
      answer:
        'Payments are deposited safely in your bank account on time. You can track payment status in your dashboard.',
    },
    {
      question: 'Are my bank details safe?',
      answer:
        'Yes, your bank details are encrypted and stored securely. We follow industry-standard security practices.',
    },
  ];

  return (
    <div className="w-full">
      <div className="max-w-2xl mx-auto">
        <form onSubmit={bankVerified ? handleSubmit : handleVerifyBank} className="space-y-6">

          {/* Bank Details Input (when not yet verified) */}
          {!bankVerified && (
            <>
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <p className="text-sm text-yellow-800 font-medium">
                  ℹ️ Bank account should be in the name as per on Enrolment ID / UIN
                </p>
              </div>

              <div className="bg-white rounded-lg p-6 border border-gray-200 space-y-4">
                <InputField
                  label="Account Number"
                  type="password"
                  name="accountNumber"
                  value={formData.accountNumber || ''}
                  onChange={handleAccountChange}
                  placeholder="Enter account number"
                  error={errors.accountNumber}
                  required
                />

                <InputField
                  label="Confirm Account Number"
                  type="password"
                  name="confirmAccountNumber"
                  value={formData.confirmAccountNumber || ''}
                  onChange={handleConfirmAccountChange}
                  placeholder="Re-enter account number"
                  error={errors.confirmAccountNumber}
                  required
                />

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    IFSC Code <span className="text-red-500">*</span>
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      name="ifscCode"
                      value={formData.ifscCode || ''}
                      onChange={handleIFSCChange}
                      placeholder="e.g., SBIN0001234"
                      maxLength="11"
                      className={`
                        flex-1 px-4 py-2 border border-gray-300 rounded-lg
                        focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent
                        ${errors.ifscCode ? 'border-red-500 bg-red-50' : ''}
                      `}
                    />
                    <a
                      href="https://www.ifsc.nbin.in/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium text-sm text-gray-700 transition whitespace-nowrap"
                    >
                      Find IFSC
                    </a>
                  </div>
                  {errors.ifscCode && (
                    <p className="text-red-500 text-sm mt-1">{errors.ifscCode}</p>
                  )}
                </div>
              </div>

              {errors.bankDetails && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-red-600 text-sm">{errors.bankDetails}</p>
                </div>
              )}
            </>
          )}

          {/* Verified State */}
          {bankVerified && (
            <>
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                <p className="text-green-700 font-semibold">
                  ✓ Bank account verified successfully!
                </p>
              </div>

              <div className="bg-white rounded-lg p-6 border border-gray-200 space-y-4">
                <div>
                  <label className="text-sm text-gray-600">Beneficiary Name</label>
                  <p className="text-lg font-semibold text-gray-800">
                    {formData.bankDetails?.beneficiaryName}
                  </p>
                </div>
                <div>
                  <label className="text-sm text-gray-600">Account</label>
                  <p className="text-lg font-semibold text-gray-800">
                    {formData.bankDetails?.bankName} - ****{formData.bankDetails?.accountNumber?.slice(-4)}
                  </p>
                </div>
                <div>
                  <label className="text-sm text-gray-600">IFSC Code</label>
                  <p className="text-lg font-semibold text-gray-800">
                    {formData.bankDetails?.ifscCode}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setBankVerified(false)}
                  className="w-full py-2 px-4 border border-gray-300 rounded-lg font-semibold text-gray-700 hover:bg-gray-50"
                >
                  Change Bank Details
                </button>
              </div>
            </>
          )}

          {/* Action Buttons */}
          <div className="flex gap-4">
            <button
              type="button"
              onClick={onPrev}
              className="flex-1 py-2 px-4 border border-gray-300 rounded-lg font-semibold text-gray-700 hover:bg-gray-50 transition"
            >
              Back
            </button>
            <button
              type="submit"
              disabled={loading || (!bankVerified && !formData.accountNumber)}
              className={`
                flex-1 py-2 px-4 rounded-lg font-semibold text-white transition duration-200
                ${loading || (!bankVerified && !formData.accountNumber)
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-700'
                }
              `}
            >
              {loading ? 'Verifying...' : bankVerified ? 'Continue' : 'Verify Bank Details'}
            </button>
          </div>
        </form>
      </div>

      <div className="mt-8">
        <FAQSection faqs={bankFAQs} />
      </div>
    </div>
  );
};

export default BankDetailsForm;

